import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tag, message, Modal, Image, Tooltip, Form, Input, Select, DatePicker } from 'antd';
import { EyeOutlined, StopOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import axios from '../utils/axios';
import dayjs from 'dayjs';
import { authService } from '../services/authService';

const { RangePicker } = DatePicker;
const { Option } = Select;

  interface LaborSignature {
  id: number;
  project_id: number;
  user_id: number;
  doctor_id: number;
  survey_id: number;
  signature_time: string;
  signature_image_url?: string;
  ip_address?: string;
  user_agent?: string;
  status: 'signed' | 'expired' | 'revoked';
  wechat_nickname?: string;
  wechat_avatar?: string;
  project?: {
    id: number;
    name: string;
  };
  user?: {
    id: number;
    username: string;
  };
  doctor?: {
    id: number;
    name: string;
    department?: string;
  };
  survey?: {
    id: number;
    title: string;
  };
}

const LaborSignatureManagement: React.FC = () => {
  const [signatures, setSignatures] = useState<LaborSignature[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<LaborSignature | null>(null);
  
  // 获取当前用户信息
  const currentUser = authService.getCurrentUser();
  const isProjectAdmin = currentUser?.role === 'project_admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [searchForm] = Form.useForm();

  // 状态颜色映射
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'signed': return 'green';
      case 'expired': return 'orange';
      case 'revoked': return 'red';
      default: return 'default';
    }
  };

  // 状态文本映射
  const getStatusText = (status: string) => {
    switch (status) {
      case 'signed': return '已签署';
      case 'expired': return '已过期';
      case 'revoked': return '已撤销';
      default: return '未知';
    }
  };

  // 获取签署记录列表
  const fetchSignatures = async (page = 1, size = 10, filters?: any) => {
    try {
      setLoading(true);
      const params: any = {
        page,
        pageSize: size,
        ...filters
      };

      const response = await axios.get('/agreement-signatures', { params });
      
      if (response.status === 200 && response.data.success) {
        setSignatures(response.data.data.signatures);
        setTotal(response.data.data.total);
        setCurrentPage(page);
        setPageSize(size);
      } else {
        message.error('获取签署记录失败');
      }
    } catch (error) {
      console.error('获取签署记录失败:', error);
      message.error('获取签署记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 撤销签署记录
  const revokeSignature = async (id: number) => {
    Modal.confirm({
      title: '确认撤销',
      content: '确定要撤销此签署记录吗？撤销后无法恢复。',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await axios.patch(`/agreement-signatures/${id}/revoke`);
          if (response.status === 200 && response.data.success) {
            message.success('签署记录已撤销');
            fetchSignatures(currentPage, pageSize);
          } else {
            message.error(response.data.message || '撤销失败');
          }
        } catch (error) {
          console.error('撤销签署记录失败:', error);
          message.error('撤销签署记录失败');
        }
      }
    });
  };

  // 查看详情
  const viewDetail = (signature: LaborSignature) => {
    setSelectedSignature(signature);
    setDetailModalVisible(true);
  };

  // 搜索处理
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const filters: any = {};
    
    if (values.project_id) filters.project_id = values.project_id;
    if (values.status) filters.status = values.status;
    if (values.timeRange && values.timeRange.length === 2) {
      filters.start_time = values.timeRange[0].format('YYYY-MM-DD');
      filters.end_time = values.timeRange[1].format('YYYY-MM-DD');
    }
    
    fetchSignatures(1, pageSize, filters);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    fetchSignatures(1, pageSize);
  };

  useEffect(() => {
    fetchSignatures();
  }, []);

  const columns: ColumnsType<LaborSignature> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '项目名称',
      key: 'project',
      render: (_, record) => record.project?.name || '-',
    },
    {
      title: '问卷标题',
      key: 'survey',
      render: (_, record) => record.survey?.title || '-',
    },
    {
      title: '医生信息',
      key: 'doctor',
      render: (_, record) => (
        <div>
          <div>{record.doctor?.name || '-'}</div>
          {record.doctor?.department && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.doctor.department}</div>
          )}
        </div>
      ),
    },
    {
      title: '签署用户',
      key: 'user',
      render: (_, record) => record.user?.username || '-',
    },
    {
      title: '微信用户',
      key: 'wechat_user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {record.wechat_avatar && (
            <img 
              src={record.wechat_avatar} 
              alt="头像" 
              style={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%',
                objectFit: 'cover'
              }} 
            />
          )}
          <span>{record.wechat_nickname || '未获取'}</span>
        </div>
      ),
    },
    {
      title: '签署时间',
      dataIndex: 'signature_time',
      key: 'signature_time',
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => ip || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button 
              type="primary" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => viewDetail(record)}
            />
          </Tooltip>
          {record.status === 'signed' && (
            <Tooltip title="撤销签署">
              <Button 
                danger 
                size="small" 
                icon={<StopOutlined />}
                onClick={() => revokeSignature(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="劳务签署管理" style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="project_id" label="项目ID">
            <Input placeholder="请输入项目ID" style={{ width: 120 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="请选择状态" style={{ width: 120 }} allowClear>
              <Option value="signed">已签署</Option>
              <Option value="expired">已过期</Option>
              <Option value="revoked">已撤销</Option>
            </Select>
          </Form.Item>
          <Form.Item name="timeRange" label="签署时间">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Table
          columns={columns}
          dataSource={signatures}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, size) => {
              fetchSignatures(page, size);
            },
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="签署记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedSignature && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h4>基本信息</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                <div><strong>签署ID:</strong> {selectedSignature.id}</div>
                <div><strong>状态:</strong> 
                  <Tag color={getStatusColor(selectedSignature.status)} style={{ marginLeft: 8 }}>
                    {getStatusText(selectedSignature.status)}
                  </Tag>
                </div>
                <div><strong>项目:</strong> {selectedSignature.project?.name || '-'}</div>
                <div><strong>问卷:</strong> {selectedSignature.survey?.title || '-'}</div>
                <div><strong>医生:</strong> {selectedSignature.doctor?.name || '-'}</div>
                <div><strong>用户:</strong> {selectedSignature.user?.username || '-'}</div>
                <div><strong>微信用户:</strong> 
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    {selectedSignature.wechat_avatar && (
                      <img 
                        src={selectedSignature.wechat_avatar} 
                        alt="头像" 
                        style={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }} 
                      />
                    )}
                    <span>{selectedSignature.wechat_nickname || '未获取'}</span>
                  </div>
                </div>
                <div><strong>签署时间:</strong> {dayjs(selectedSignature.signature_time).format('YYYY-MM-DD HH:mm:ss')}</div>
                <div><strong>IP地址:</strong> {selectedSignature.ip_address || '-'}</div>
              </div>
            </div>

            {selectedSignature.user_agent && (
              <div style={{ marginBottom: 16 }}>
                <h4>浏览器信息</h4>
                <div style={{ fontSize: 12, color: '#666', wordBreak: 'break-all' }}>
                  {selectedSignature.user_agent}
                </div>
              </div>
            )}

            {selectedSignature.signature_image_url && (
              <div>
                <h4>签名图片</h4>
                <Image
                  src={selectedSignature.signature_image_url}
                  alt="签名"
                  style={{ maxWidth: '100%', border: '1px solid #d9d9d9' }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LaborSignatureManagement; 